import { useState, type FC } from "react";
import type { Option } from "../../types";
import type { InputProps } from "../../DynamicInput";
import type { CreateNewDialogProps } from "../../shared/MultiAutocomplete";
import { MultiAutocomplete } from "../../shared/MultiAutocomplete";
import { CalendarDaysIcon, MeasureIcon3 } from "../../../shared/icons";
import { usePermission } from "../../../shared/permissions";
import { inlineInputStateStyles as inlineInputState } from "./InlineInputState";
import * as FormDialog from "../../../shared/FormDialog";
import { useTranslation } from "@privacy/translations";
import { useGetUsersFromCurrentOrganisation } from "../../../api/users";
import { createMeasureMutationOptions } from "../../../api/measures";
import type { CreateMeasurePayload } from "../../../api/measures.types";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { Box, FormControl, Typography } from "@mui/material";
import { UserSelect } from "../../../shared/UserSelect";
import { DatePicker } from "../../../shared/DatePicker";
import { useMutation } from "@tanstack/react-query";
import { HACK_alignIconWithLabel } from "@privacy/ui";

type MeasureProps = InputProps<string[]> & { options: Option[] };

export const MeasureMultiSelect: FC<MeasureProps> = ({ options, ...props }) => {
  const { hasPermission } = usePermission();
  return (
    <MultiAutocomplete
      {...props}
      options={options}
      renderIcon={(_, option) => (option ? <MeasureIcon3 /> : undefined)}
      inlineInputStateStyles={inlineInputState}
      chipVariant="entity"
      noOptionsTextKey="DynamicForm.Option.Empty"
      placeholderKey="DynamicForm.Inline.InputPlaceholder"
      {...(hasPermission("nova-forms-api:dpia:create")
        ? { CreateNewDialog: CreateNewMeasureDialog }
        : { noOptionsTextKey: "DynamicForm.Option.Empty" })}
    />
  );
};

const CreateNewMeasureDialog: FC<CreateNewDialogProps> = ({ onClose, value, onCreated }) => (
  <CreateMeasureDialog
    onClose={onClose}
    onCreated={measure => onCreated({ value: measure.id, label: measure.name })}
    defaultName={value}
  />
);

type CreateMeasureDialogProps = {
  onClose: () => void;
  onCreated: (measure: { name: string; id: string }) => void;
  defaultName?: string;
};

const CreateMeasureDialog: FC<CreateMeasureDialogProps> = ({ onClose, onCreated, defaultName = "" }) => {
  const { t } = useTranslation();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const { data } = useGetUsersFromCurrentOrganisation();
  const createMeasureMutation = useMutation(createMeasureMutationOptions());

  const { handleSubmit, reset, control, setFocus } = useForm<CreateMeasurePayload>({
    defaultValues: { name: defaultName, description: "", responsible: "", dueDate: undefined },
  });

  const handleCancel = () => {
    onClose();
    reset();
  };

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleNewMeasure = ({ name, description, responsible, dueDate }: CreateMeasurePayload) =>
    createMeasureMutation.mutate(
      { name, description, responsible, dueDate },
      {
        onSuccess: async ({ id }) => {
          handleCancel();
          onCreated({ name, id });
        },
      }
    );

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreateMeasure.Dialog.Title")}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(handleNewMeasure)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={createMeasureMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreateMeasure.Dialog.Validation.NameRequired") }}
              label={t("CreateMeasure.Dialog.NameLabel")}
              placeholder={t("CreateMeasure.Dialog.NamePlaceholder")}
            />
            <FormDialog.TextField
              control={control}
              name="description"
              label={t("CreateMeasure.Dialog.DescriptionLabel")}
              placeholder={t("CreateMeasure.Dialog.DescriptionPlaceholder")}
              multiline
              rows={4}
            />
            <FormDialog.ControlledField
              control={control}
              name="responsible"
              label={t("CreateMeasure.Dialog.ResponsibleLabel")}
            >
              {({ field: { ref, onChange, value }, id }) => (
                <UserSelect
                  enableCreateNewUser
                  id={id}
                  selectedId={value}
                  ref={ref}
                  onBlur={undefined}
                  users={data?.users}
                  onSelectUser={onChange}
                />
              )}
            </FormDialog.ControlledField>
            <FormDialog.ControlledField control={control} name="dueDate" label={t("CreateMeasure.Dialog.DueDateLabel")}>
              {({ field: { ref, ...field }, id }) => (
                <FormControl fullWidth>
                  <DatePicker
                    {...field}
                    onChange={value => field.onChange(value ? value.format("YYYY-MM-DD") : undefined)}
                    value={field.value ? dayjs(field.value) : null}
                    hideStartAdornment={!field.value}
                    disablePast
                    format="DD MMMM YYYY"
                    closeOnSelect
                    inputRef={ref}
                    open={isDatePickerOpen}
                    onClose={() => setIsDatePickerOpen(false)}
                    slotProps={{
                      textField: props => ({
                        id,
                        size: "small",
                        onFocus: e => e.target.blur(),
                        onClick: () => !props.isFieldDisabled && setIsDatePickerOpen(true),
                        slotProps: {
                          input: {
                            startAdornment: !field.value ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CalendarDaysIcon size={16} color={theme => theme.palette.text.placeholder} />
                                <Typography
                                  sx={{
                                    fontSize: theme => theme.typography.pxToRem(14),
                                    color: theme => theme.palette.text.placeholder,
                                    ...HACK_alignIconWithLabel,
                                  }}
                                >
                                  {t("CreateMeasure.Dialog.DueDatePlaceholder")}
                                </Typography>
                              </Box>
                            ) : undefined,
                          },
                        },
                      }),
                    }}
                  />
                </FormControl>
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton isSubmitting={createMeasureMutation.isPending}>
            {t("CreateMeasure.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
